// Admin Photos Tab — manage gallery photos for the homepage
// Upload, list, toggle on_home, reorder, delete
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveAsset = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

const EXT_TO_TYPE = (url = "") => {
  const ext = (url.split("?")[0].split(".").pop() || "").toLowerCase();
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) return "image";
  if (["mp3", "wav", "ogg", "m4a", "aac", "flac"].includes(ext)) return "audio";
  if (["mp4", "mov", "webm", "avi", "mkv", "m4v"].includes(ext)) return "video";
  return "image";
};

export default function PhotosTab({ locale }) {
  const isFr = locale === "fr";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total, current }
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all"); // all | home | fetes | unused | image | audio | video

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/photos");
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/photos");
        if (!cancelled) setItems(data.items || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Handle MULTIPLE files at once. Each file is uploaded sequentially with
  // progress feedback, and a corresponding photo entry is created.
  const handleUpload = async (ev) => {
    const files = Array.from(ev.target.files || []);
    if (files.length === 0) return;
    setErr("");
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length, current: files[0]?.name || "" });
    const maxOrder = items.reduce((m, x) => Math.max(m, x.order || 0), 0);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress({ done: i, total: files.length, current: file.name });
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data: up } = await api.post("/admin/uploads/media", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const mt = up.media_type || EXT_TO_TYPE(up.url);
          await api.post("/admin/photos", {
            image_url: up.url,
            caption: { fr: "", en: "" },
            on_home: mt === "image",
            on_fetes: false,
            media_type: mt,
            order: maxOrder + i + 1,
          });
        } catch (fileErr) {
          // continue with next files but surface the error
          setErr(`${file.name} : ${fileErr?.response?.data?.detail || "erreur"}`);
        }
      }
    } finally {
      setUploadProgress(null);
      setUploading(false);
      ev.target.value = "";
      load();
    }
  };

  const toggleFlag = async (p, key) => {
    await api.patch(`/admin/photos/${p.id}`, { [key]: !p[key] });
    load();
  };

  const remove = async (p) => {
    if (!window.confirm(isFr ? "Supprimer ce média ?" : "Delete this media?")) return;
    await api.delete(`/admin/photos/${p.id}`);
    load();
  };

  // Drag-and-drop reordering. On drop we recompute monotonic order values
  // for the current filtered view and PATCH the items whose order changed.
  const [dragId, setDragId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);

  const onDragStart = (e, p) => {
    setDragId(p.id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", p.id); } catch (_) { /* ignore */ }
  };
  const onDragOver = (e, p) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (p.id !== dropTargetId) setDropTargetId(p.id);
  };
  const onDragEnd = () => { setDragId(null); setDropTargetId(null); };
  const onDrop = async (e, target) => {
    e.preventDefault();
    const draggedId = dragId || e.dataTransfer.getData("text/plain");
    setDragId(null); setDropTargetId(null);
    if (!draggedId || draggedId === target.id) return;
    // Work on the *filtered* visible list so DnD respects the active filter
    const arr = [...filtered];
    const fromIdx = arr.findIndex((x) => x.id === draggedId);
    const toIdx = arr.findIndex((x) => x.id === target.id);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, moved);
    // Reassign monotonic order values starting at 1
    const updates = arr.map((item, i) => ({ id: item.id, newOrder: i + 1, prev: item.order || 0 }));
    const toPatch = updates.filter((u) => u.newOrder !== u.prev);
    // Optimistic update
    setItems((prev) => prev.map((it) => {
      const u = updates.find((x) => x.id === it.id);
      return u ? { ...it, order: u.newOrder } : it;
    }));
    try {
      await Promise.all(toPatch.map((u) => api.patch(`/admin/photos/${u.id}`, { order: u.newOrder })));
    } finally { load(); }
  };

  const sorted = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
  const filtered = sorted.filter((p) => {
    const mt = p.media_type || EXT_TO_TYPE(p.image_url);
    if (filter === "home") return p.on_home;
    if (filter === "fetes") return p.on_fetes;
    if (filter === "gallery") return p.on_gallery;
    if (filter === "unused") return !p.on_home && !p.on_fetes && !p.on_gallery;
    if (filter === "image" || filter === "audio" || filter === "video") return mt === filter;
    return true;
  });
  const onHomeCount = items.filter((x) => x.on_home).length;
  const onFetesCount = items.filter((x) => x.on_fetes).length;
  const onGalleryCount = items.filter((x) => x.on_gallery).length;
  const unusedCount = items.filter((x) => !x.on_home && !x.on_fetes && !x.on_gallery).length;
  const imgCount = items.filter((x) => (x.media_type || EXT_TO_TYPE(x.image_url)) === "image").length;
  const audioCount = items.filter((x) => (x.media_type || EXT_TO_TYPE(x.image_url)) === "audio").length;
  const videoCount = items.filter((x) => (x.media_type || EXT_TO_TYPE(x.image_url)) === "video").length;

  return (
    <div data-testid="photos-tab" className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl">{isFr ? "Audiovisuel" : "Audio · Video"}</h2>
          <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
            {isFr
              ? "Bibliothèque sans limite : photos, audio, vidéo. Activez ★ Accueil pour le diaporama de la home et ✦ Fêtes pour celui des Fêtes 2027. Téléversement multiple supporté. Glissez-déposez les vignettes pour les réordonner."
              : "Unlimited library: photos, audio, video. Toggle ★ Home for the landing slideshow and ✦ Fêtes for the Fêtes 2027 page. Bulk upload supported. Drag-and-drop to reorder."}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
            {imgCount} 🖼 · {audioCount} ♪ · {videoCount} ▶ · {items.length} {isFr ? "TOTAL" : "TOTAL"}
          </span>
          <button className="btn btn-outline btn-sm"
                  data-testid="photo-migrate-legacy-btn"
                  onClick={async () => {
                    if (!window.confirm(isFr
                      ? "Re-uploader toutes les photos legacy (/uploads/...) vers Object Storage ?"
                      : "Re-upload all legacy /uploads photos to Object Storage?")) return;
                    try {
                      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/photos/migrate-legacy`, {
                        method: "POST", credentials: "include",
                      });
                      const data = await r.json();
                      alert((isFr ? "Migration terminée — " : "Migration done — ")
                        + `${data.migrated} ${isFr ? "migrées" : "migrated"} · `
                        + `${data.missing} ${isFr ? "manquantes" : "missing"}`);
                      window.location.reload();
                    } catch (e) {
                      alert(isFr ? "Erreur : " + e.message : "Error: " + e.message);
                    }
                  }}>
            ↻ {isFr ? "Migrer legacy → S3" : "Migrate legacy → S3"}
          </button>
          <label className="btn btn-primary cursor-pointer" data-testid="photo-upload-label">
            {uploading
              ? (uploadProgress
                  ? `${uploadProgress.done + 1}/${uploadProgress.total}…`
                  : "…")
              : "+ " + (isFr ? "Téléverser (plusieurs)" : "Upload (multiple)")}
            <input type="file" multiple accept=".png,.jpg,.jpeg,.webp,.gif,.mp3,.wav,.ogg,.m4a,.aac,.flac,.mp4,.mov,.webm,.avi,.mkv,.m4v"
                   className="hidden"
                   data-testid="photo-upload" onChange={handleUpload} disabled={uploading} />
          </label>
        </div>
      </div>

      {uploadProgress && (
        <div className="bg-white border p-3" data-testid="upload-progress"
             style={{ borderColor: "var(--gold)" }}>
          <div className="font-caps text-[0.6rem] mb-2" style={{ color: "var(--gold)", letterSpacing: "0.18em" }}>
            {isFr ? "TÉLÉVERSEMENT" : "UPLOAD"} {uploadProgress.done + 1} / {uploadProgress.total}
          </div>
          <div className="font-mono text-xs truncate" style={{ color: "var(--ink-2)" }}>{uploadProgress.current}</div>
          <div className="mt-2 h-1 bg-gray-100 rounded overflow-hidden">
            <div className="h-full transition-all" style={{
              width: `${((uploadProgress.done + 0.5) / uploadProgress.total) * 100}%`,
              background: "var(--gold)",
            }} />
          </div>
        </div>
      )}

      {/* Filter chips */}
      <div className="flex gap-1 border w-fit flex-wrap" style={{ borderColor: "var(--border)" }}>
        {[
          { k: "all", label: isFr ? "Tous" : "All", count: items.length },
          { k: "home", label: isFr ? "★ Accueil" : "★ Home", count: onHomeCount },
          { k: "fetes", label: "✦ Fêtes", count: onFetesCount },
          { k: "gallery", label: isFr ? "❖ Galerie" : "❖ Gallery", count: onGalleryCount },
          { k: "image", label: "🖼 " + (isFr ? "Photos" : "Photos"), count: imgCount },
          { k: "audio", label: "♪ Audio", count: audioCount },
          { k: "video", label: "▶ Vidéo", count: videoCount },
          { k: "unused", label: isFr ? "Non utilisés" : "Unused", count: unusedCount },
        ].map((x) => (
          <button key={x.k} data-testid={`photo-filter-${x.k}`}
                  className="font-caps text-[0.6rem] px-3 py-1.5"
                  style={{
                    background: filter === x.k ? "var(--ink)" : "transparent",
                    color: filter === x.k ? "var(--parchment)" : "var(--ink)",
                    letterSpacing: "0.18em",
                  }}
                  onClick={() => setFilter(x.k)}>
            {x.label} · {x.count}
          </button>
        ))}
      </div>

      {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
      {editing && (
        <PhotoEditor isFr={isFr} initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}

      {loading && <div className="font-display italic" style={{ color: "var(--muted)" }}>…</div>}

      {!loading && filtered.length === 0 && (
        <div className="font-body text-sm italic" style={{ color: "var(--muted)" }}>
          {isFr ? "Aucun média dans cette vue." : "No media in this view."}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photos-grid">
          {filtered.map((p, i) => {
            const mt = p.media_type || EXT_TO_TYPE(p.image_url);
            const src = resolveAsset(p.image_url);
            const filename = (p.image_url || "").split("/").pop() || "media";
            return (
              <div key={p.id} data-testid={`photo-${p.id}`}
                   className="bg-white border overflow-hidden transition-all"
                   draggable
                   onDragStart={(e) => onDragStart(e, p)}
                   onDragOver={(e) => onDragOver(e, p)}
                   onDrop={(e) => onDrop(e, p)}
                   onDragEnd={onDragEnd}
                   style={{
                     borderColor: dropTargetId === p.id ? "var(--cinnabar)"
                       : (p.on_home || p.on_fetes ? "var(--gold)" : "var(--border)"),
                     borderWidth: dropTargetId === p.id || p.on_home || p.on_fetes ? 2 : 1,
                     opacity: dragId === p.id ? 0.4 : 1,
                     cursor: "grab",
                   }}>
                <div className="relative" style={{ aspectRatio: "4/3", overflow: "hidden", background: "var(--parchment-2)" }}>
                  {mt === "image" && (
                    <img src={src} alt={p.caption?.fr || p.caption?.en || ""} className="w-full h-full object-cover" />
                  )}
                  {mt === "audio" && (
                    <div className="w-full h-full flex flex-col items-center justify-center p-3 gap-2">
                      <div className="text-4xl" style={{ color: "var(--lapis)" }}>♪</div>
                      <audio controls src={src} className="w-full" preload="none" />
                    </div>
                  )}
                  {mt === "video" && (
                    <video src={src} className="w-full h-full object-cover" controls preload="metadata" />
                  )}
                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {p.on_home && (
                      <span className="font-caps text-[0.55rem] px-2 py-0.5"
                            style={{ background: "var(--gold)", color: "var(--ink)", letterSpacing: "0.18em" }}>
                        ★ {isFr ? "ACCUEIL" : "HOME"}
                      </span>
                    )}
                    {p.on_fetes && (
                      <span className="font-caps text-[0.55rem] px-2 py-0.5"
                            style={{ background: "var(--cinnabar)", color: "#fff", letterSpacing: "0.18em" }}>
                        ✦ FÊTES
                      </span>
                    )}
                    {p.on_gallery && (
                      <span className="font-caps text-[0.55rem] px-2 py-0.5"
                            style={{ background: "var(--lapis)", color: "#fff", letterSpacing: "0.18em" }}>
                        ❖ {isFr ? "GALERIE" : "GALLERY"}
                      </span>
                    )}
                  </div>
                  <span className="absolute top-2 right-2 font-caps text-[0.5rem] px-1.5 py-0.5"
                        style={{ background: "rgba(255,255,255,0.9)", color: "var(--ink)", letterSpacing: "0.18em" }}>
                    {mt === "image" ? "IMG" : mt === "audio" ? "AUDIO" : "VIDEO"}
                  </span>
                </div>
                <div className="p-3 space-y-2">
                  {(p.caption?.fr || p.caption?.en) ? (
                    <p className="font-body text-sm leading-tight">{p.caption?.fr || p.caption?.en}</p>
                  ) : (
                    <p className="font-body text-xs italic" style={{ color: "var(--muted)" }}>
                      {isFr ? "Sans légende" : "No caption"}
                    </p>
                  )}
                  <div className="flex gap-1 flex-wrap">
                    <button className="btn btn-outline btn-xs"
                            data-testid={`photo-toggle-home-${p.id}`}
                            onClick={() => toggleFlag(p, "on_home")}
                            title={isFr ? "Accueil" : "Home"}
                            disabled={mt !== "image"}
                            style={p.on_home ? { color: "var(--gold)", borderColor: "var(--gold)" } : undefined}>
                      {p.on_home ? "★" : "☆"}
                    </button>
                    <button className="btn btn-outline btn-xs"
                            data-testid={`photo-toggle-fetes-${p.id}`}
                            onClick={() => toggleFlag(p, "on_fetes")}
                            title={isFr ? "Fêtes" : "Fêtes"}
                            disabled={mt !== "image"}
                            style={p.on_fetes ? { color: "var(--cinnabar)", borderColor: "var(--cinnabar)" } : undefined}>
                      ✦
                    </button>
                    <button className="btn btn-outline btn-xs"
                            data-testid={`photo-toggle-gallery-${p.id}`}
                            onClick={() => toggleFlag(p, "on_gallery")}
                            title={isFr ? "Galerie publique" : "Public Gallery"}
                            disabled={mt !== "image"}
                            style={p.on_gallery ? { color: "var(--lapis)", borderColor: "var(--lapis)" } : undefined}>
                      ❖
                    </button>
                    <a className="btn btn-outline btn-xs"
                       href={src}
                       download={filename}
                       data-testid={`photo-download-${p.id}`}
                       title={isFr ? "Télécharger" : "Download"}>
                      ⤓
                    </a>
                    <button className="btn btn-outline btn-xs"
                            data-testid={`photo-edit-${p.id}`}
                            onClick={() => setEditing({ ...p })}>✎</button>
                    <button className="btn btn-outline btn-xs"
                            data-testid={`photo-delete-${p.id}`}
                            style={{ color: "var(--cinnabar)" }}
                            onClick={() => remove(p)}>✕</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PhotoEditor({ initial, isFr, onCancel, onSaved }) {
  const [p, setP] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const setCap = (lang, val) => setP((s) => ({ ...s, caption: { ...(s.caption || {}), [lang]: val } }));
  const save = async () => {
    setErr("");
    setBusy(true);
    try {
      await api.patch(`/admin/photos/${p.id}`, {
        caption: p.caption,
        on_home: p.on_home,
        on_fetes: p.on_fetes,
        on_gallery: p.on_gallery,
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };
  return (
    <div className="admin-card space-y-4" data-testid="photo-editor">
      <h3 className="font-display text-xl">{isFr ? "Modifier la photo" : "Edit photo"}</h3>
      <img src={resolveAsset(p.image_url)} alt="" className="max-h-48 object-contain border" style={{ borderColor: "var(--border)" }} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="label-caps">{isFr ? "Légende FR" : "Caption FR"}</span>
          <input className="input-classic mt-1" value={p.caption?.fr || ""} onChange={(e) => setCap("fr", e.target.value)} data-testid="photo-caption-fr" />
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Légende EN" : "Caption EN"}</span>
          <input className="input-classic mt-1" value={p.caption?.en || ""} onChange={(e) => setCap("en", e.target.value)} data-testid="photo-caption-en" />
        </label>
      </div>
      <div className="flex gap-6 flex-wrap">
        <label className="flex items-center gap-2 font-body">
          <input type="checkbox" checked={!!p.on_home} onChange={(e) => setP((s) => ({ ...s, on_home: e.target.checked }))} data-testid="photo-onhome" />
          {isFr ? "★ Page d'accueil" : "★ Home page"}
        </label>
        <label className="flex items-center gap-2 font-body">
          <input type="checkbox" checked={!!p.on_fetes} onChange={(e) => setP((s) => ({ ...s, on_fetes: e.target.checked }))} data-testid="photo-onfetes" />
          {isFr ? "✦ Page Fêtes 2027" : "✦ Fêtes 2027 page"}
        </label>
        <label className="flex items-center gap-2 font-body">
          <input type="checkbox" checked={!!p.on_gallery} onChange={(e) => setP((s) => ({ ...s, on_gallery: e.target.checked }))} data-testid="photo-ongallery" />
          {isFr ? "❖ Galerie publique" : "❖ Public Gallery"}
        </label>
      </div>
      {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
      <div className="flex gap-3 justify-end">
        <button className="btn btn-outline" onClick={onCancel} data-testid="photo-cancel">{isFr ? "Annuler" : "Cancel"}</button>
        <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="photo-save">
          {busy ? "…" : (isFr ? "Enregistrer" : "Save")}
        </button>
      </div>
    </div>
  );
}
